import { Agent, ExtractorDocumentType, OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { createContext, ReactElement, useContext, useReducer } from 'react';

import { CheckoutSessionDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionDetailEntity';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { StoredBorrowerOnboardingDetails } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';

export interface ExtractedFileEntry {
  file: File;
  documentType?: ExtractorDocumentType;
}

export interface MerchantPosInitialData {
  agent: Agent;
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  checkoutSessionDetailEntity: CheckoutSessionDetailEntity;
  borrowerOnboardingDetails: StoredBorrowerOnboardingDetails[];
  selectedRole: OnboardingDetailsRole | undefined;
  isEditing: boolean;
  disableSaveButton?: boolean;
  onSaveDetails?: (onboardingDetails: StoredBorrowerOnboardingDetails) => Promise<void>;
  beforeSubmit?: () => Promise<void>;
}

const InitialState = (data: MerchantPosInitialData) => ({
  ...data,
  onSave: async () => {},
  extractedFiles: [] as ExtractedFileEntry[],
});

type MerchantPosContextInitialState = ReturnType<typeof InitialState>;

const reducer = (state: MerchantPosContextInitialState, newState: Partial<MerchantPosContextInitialState>) => ({
  ...state,
  ...newState,
});

const useSectionReducer = (data: MerchantPosInitialData) => {
  const [state, dispatch] = useReducer(reducer, InitialState(data));

  const update = (newContext: Partial<MerchantPosContextInitialState>) => dispatch(newContext);

  return { ...state, update };
};

type MerchantPosContextState = ReturnType<typeof useSectionReducer>;

const MerchantPosContext = createContext<MerchantPosContextState | null>(null);

interface MerchantPosContextProviderProps {
  data: MerchantPosInitialData;
  children: ReactElement;
}

const MerchantPosContextProvider = ({ children, data }: MerchantPosContextProviderProps) => {
  const { ...state } = useSectionReducer(data);

  return <MerchantPosContext.Provider value={{ ...state }}>{children}</MerchantPosContext.Provider>;
};

const useMerchantPosContext = (): MerchantPosContextState => {
  const context = useContext(MerchantPosContext);

  if (!context) {
    throw new Error('useMerchantPos Context must be used within an MerchantPosContextProvider');
  }

  return context;
};

export { MerchantPosContextProvider, useMerchantPosContext };
